//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { IdentifierRow } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/catalog-search-modal/IdentifierRow';
import { VariantsTable } from '@campaigns/components/shared/product-details-modal/drawer/variants/VariantsTable';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useProductSearch } from '@campaigns/hooks/useProductSearch';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { doImageUrlsMatch } from '@campaigns/utils/sampling/SamplingImageUtils';
import { Badge, BadgeType } from '@components/atoms/badge/Badge';
import { DefaultButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { CaptionText, SubsectionText } from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';

export const DEFAULT_VARIATION = {
  fairMarketValue: undefined,
  imgUrl: undefined,
  logisticsRef: undefined,
  quantity: undefined,
  variantName: undefined,
};

interface VariantsTabProps {
  isViewOnly: boolean;
}

export const VariantsTab = ({ isViewOnly }: VariantsTabProps) => {
  const { control, getValues, setValue } =
    useFormContext<EditableBVSamplingProduct>();
  const {
    clearAllVariantImages,
    isImageForAllVariants,
    removeVariantImage,
    setIsImageForAllVariants,
  } = useProductModal();
  const { append, fields, remove } = useFieldArray({
    control,
    name: 'variations',
  });
  const productName = useWatch({ control, name: 'name' });
  const productImageUrl = useWatch({ control, name: 'imgUrl' });
  const watchedVariations = useWatch({ control, name: 'variations' });
  const productBrand = useWatch({ control, name: 'brand' });
  const productCategory = useWatch({ control, name: 'category' });
  const catalogProductId = useWatch({
    control,
    name: 'targetCatalogProductId',
  });
  const [debouncedCatalogProductId] = useDebounce(
    catalogProductId,
    SEARCH_DEBOUNCE_DELAY,
  );
  const { data: catalogProduct } = useProductSearch({
    productIds: debouncedCatalogProductId
      ? [debouncedCatalogProductId]
      : undefined,
    enabled: Boolean(debouncedCatalogProductId),
  });
  const upcs = catalogProduct?.products[0]?.upcs ?? [];
  const eans = catalogProduct?.products[0]?.eans ?? [];

  // Mixed state: some (but not all) variants use the parent product image
  const variantsUsingParentImageCount = watchedVariations.filter((variation) =>
    doImageUrlsMatch(variation.imgUrl, productImageUrl),
  ).length;
  const isParentImageIndeterminate =
    !isImageForAllVariants &&
    variantsUsingParentImageCount > 0 &&
    variantsUsingParentImageCount < watchedVariations.length;

  const handleAddVariation = () => {
    const currentValues = getValues();
    const mainImgUrl = currentValues.imgUrl;

    // With "Use for all variants" on, new variants inherit the main image so the user needn't
    // set one manually (which would toggle the flag off).
    const newVariation =
      isImageForAllVariants && mainImgUrl
        ? { ...DEFAULT_VARIATION, imgUrl: mainImgUrl }
        : { ...DEFAULT_VARIATION };

    // append/remove (rather than setValue) preserve existing rows' field-array IDs,
    // which VariantsTable relies on to keep expansion state stable.
    append(newVariation, { shouldFocus: false });
  };

  const handleRemoveVariation = (index: number) => {
    const currentValues = getValues();
    const removedImgUrl = currentValues.variations[index]?.imgUrl;

    // Revoke the form-owned blob URL (created on upload in VariantRow) if it isn't
    // also in use by the main image or another variant, before it becomes unreachable.
    if (removedImgUrl?.startsWith('blob:')) {
      const isUsedByMainImage = currentValues.imgUrl === removedImgUrl;
      const isUsedByOtherVariants = currentValues.variations.some(
        (variation, idx) => idx !== index && variation.imgUrl === removedImgUrl,
      );
      if (!isUsedByMainImage && !isUsedByOtherVariants) {
        URL.revokeObjectURL(removedImgUrl);
      }
    }

    removeVariantImage(index);
    remove(index);
  };

  const handleUseParentImageForAllVariants = (checked: boolean) => {
    setIsImageForAllVariants(checked);

    const currentValues = getValues();
    if (checked && currentValues.imgUrl) {
      // Per-variant uploads become stale once the parent image applies to every row.
      clearAllVariantImages();

      // Revoke form-owned blob URLs (set via VariantRow's own upload) before they're
      // overwritten below and become unreachable to the drawer unmount cleanup.
      const oldBlobUrls = new Set(
        currentValues.variations
          .map((variation) => variation.imgUrl)
          .filter(
            (imgUrl): imgUrl is string =>
              Boolean(imgUrl?.startsWith('blob:')) &&
              imgUrl !== currentValues.imgUrl,
          ),
      );
      oldBlobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));

      // Set each row's imgUrl individually (rather than replacing the whole
      // `variations` array) to preserve field-array IDs and accordion state.
      currentValues.variations.forEach((_variation, index) => {
        setValue(`variations.${index}.imgUrl`, currentValues.imgUrl, {
          shouldValidate: true,
        });
      });
    }
  };

  return (
    <Container>
      <ProductSummary>
        <ProductImage>
          <ProductSummaryImage
            alt={productName ?? 'Product image'}
            src={productImageUrl}
          />
        </ProductImage>
        <ProductInfo>
          <ProductName>
            {catalogProductId && (
              <Badge
                size='small'
                text={catalogProductId}
                type={BadgeType.Neutral}
              />
            )}
          </ProductName>
          <SubsectionText>{productName ?? 'Product name'}</SubsectionText>
          <CaptionText>
            {[productBrand, productCategory].filter(isDefined).join(' | ')}
          </CaptionText>
          <ProductIdentifiers>
            <IdentifierRow identifiers={upcs} label='UPC' />
            <IdentifierRow identifiers={eans} label='EAN' />
          </ProductIdentifiers>
        </ProductInfo>
      </ProductSummary>
      {!isViewOnly && (
        <LabeledCheckbox
          handleClick={handleUseParentImageForAllVariants}
          isChecked={isImageForAllVariants}
          isIndeterminate={isParentImageIndeterminate}
          label='Use parent product image for all variants'
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_USE_FOR_ALL_VARIANTS_CHECKBOX
          }
        />
      )}
      <VariantsTable
        fields={fields}
        isViewOnly={isViewOnly}
        onRemoveVariant={handleRemoveVariation}
      />
      {!isViewOnly && (
        <StyledDefaultButton
          iconName='add-with-circle'
          iconSize='md'
          onClick={handleAddVariation}
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_ADD_VARIATION
          }
        >
          Add Variant
        </StyledDefaultButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const StyledDefaultButton = styled(DefaultButton)`
  align-self: flex-start;
`;

const ProductSummary = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.echo.spacing.sm};
`;

const ProductImage = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.xs};
`;

const ProductSummaryImage = styled(ImageWithErrorHandling)`
  width: ${({ theme }) => `calc(${theme.echo.spacing.xxs} * 25)`};
  height: ${({ theme }) => `calc(${theme.echo.spacing.xxs} * 25)`};
  border: 1px solid ${({ theme }) => theme.colors.gray2};
  border-radius: ${({ theme }) => theme.borderRadii[1]};
  object-fit: contain;
`;

const ProductInfo = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.xxs};
`;

const ProductName = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.xxs};
`;

const ProductIdentifiers = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
